import {ReportName} from 'common/report/constants';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {ChartPlotStyle, ChartType} from 'common/types/reports/bookmark';

import {NetworkError} from './enums';
import {DigestDateOrdinal, DigestRecurrence} from './enums/email-digest';
import {RetentionType} from './enums/report';

export const RETENTION_TYPE_TO_NAME = {
  addiction: RetentionType.Addiction,
  birth: RetentionType.FirstTime,
  compounded: RetentionType.Recurring,
};

export const MAX_CONCURRENT_QUERIES = 10;
export const TV_MODE_RELOAD_TIME_IN_MS = 10 * 60 * 1000; // 10 minutes
export const TV_MODE_ALTERNATE_VIEW_INTERVAL_MS = 15 * 1000; // 15 seconds, interval for switching between different chart views in TV mode
export const MAX_EMAIL_DIGEST_NAME_LENGTH = 80;
export const MAX_FUNNEL_QUERY_DAYS = 366;
export const FEATURE_GATES_UNLIMITED = 9223372036854776000; // python max int, aka featureGates.unlimited
export const MAX_DISPLAYED_SEGMENTS = 6;

export const DIGEST_RECURRENCE_FRIENDLY_NAME: {[digestRecurrence: number]: string} = {
  [DigestRecurrence.Daily]: `Daily`,
  [DigestRecurrence.ASAP]: `ASAP`,
  [DigestRecurrence.Weekly]: `Weekly`,
  [DigestRecurrence.Monthly]: `Monthly`,
  [DigestRecurrence.Quarterly]: `Quarterly`,
};

export const DIGEST_DATE_ORDINAL_FRIENDLY_NAME: {[digestDayNumber: number]: string} = {
  [DigestDateOrdinal.First]: `First`,
  [DigestDateOrdinal.Last]: `Last`,
};

export const ERROR_MESSAGES = {
  [NetworkError.CreateEmailDigest]: `Your Digest could not be created`,
  [NetworkError.DeleteEmailDigest]: `Your Digest could not be deleted`,
  [NetworkError.DeleteReport]: `Your board report could not be deleted`,
  [NetworkError.GetEmailDigests]: `Your Digests could not be loaded`,
  [NetworkError.GetReports]: `Your board reports could not be loaded`,
  [NetworkError.RestoreEmailDigest]: `Your Digest could not be restored`,
  [NetworkError.RestoreReport]: `Your board report could not be restored`,
  [NetworkError.UpdateChartOptions]: `Your board report chart could not be updated`,
  [NetworkError.UpdateEmailDigest]: `Your Digest failed to save`,
  [NetworkError.UpdateName]: `Your board report name failed to save`,
  [NetworkError.GetBookmarks]: `Your saved reports could not be loaded`,
  [NetworkError.AddBookmarks]: `Your selected reports could not be added to the board`,
  [NetworkError.GetProjectInfo]: `Your project info could not be loaded`,
  [NetworkError.GetUserInfo]: `Your user info could not be loaded`,
};

export const DEFAULT_DATE_RANGES = {
  [BookmarkType.Retention]: [
    {params: {date_range: {from: -13, to: 0}, date_unit: `day`}, value: `last 14 days`},
    {params: {date_range: {from: -84, to: 0}, date_unit: `week`}, value: `last 12 weeks`},
    {params: {date_range: {from: -392, to: 0}, date_unit: `month`}, value: `last 12 months`},
  ],
  [BookmarkType.Funnels]: [
    {params: {date_range: {from: 0, to: 0}}, value: `today`},
    {params: {date_range: {from: -1, to: -1}}, value: `yesterday`},
    {params: {date_range: {from: -7, to: 0}}, value: `last week`},
    {params: {date_range: {from: -30, to: 0}}, value: `last 30 days`},
    {params: {date_range: {from: -90, to: 0}}, value: `last 90 days`},
    {params: {date_range: {from: -180, to: 0}}, value: `last 6 months`},
    {params: {date_range: {from: -365, to: 0}}, value: `last 12 months`},
  ],
};

export const SHOW_TIME_FILTER = {
  CLOSED: `closed`,
  DATEPICKER: `show_datepicker`,
  DATE_DROPDOWN: `show_date_dropdown`,
};

export const GRID_MEASUREMENTS = {
  COLUMN_COUNT: 4,
  COLUMN_WIDTH: 319.5,
  COMPACT_COLUMN_WIDTH: 258.5, // pre-calculated with 16px gap for a 1150px total grid width
  GAP: 16,
  ROW_HEIGHT: 336,
};

export const CHART_TYPE_ICONS = {
  [ChartType.Bar]: `bar`,
  [ChartType.FlowsTopPaths]: `funnel-steps`,
  [ChartType.FunnelSteps]: `funnel-steps`,
  [ChartType.FunnelTrend]: `line`,
  [ChartType.FunnelMedianTTC]: `single-metric`,
  [ChartType.FunnelTTCBar]: `bar`,
  [ChartType.FunnelTTCLine]: `line`,
  [ChartType.FunnelFrequencyLine]: `line`,
  [ChartType.FunnelFrequencyBar]: `bar`,
  [ChartType.FunnelTopPaths]: `top-paths`,
  [ChartType.Line]: `line`,
  [ChartType.Column]: `column`,
  [ChartType.StackedColumn]: `column-stacked`,
  [ChartType.Metric]: `single-metric`,
  [ChartType.FunnelStepsMetric]: `single-metric`,
  [ChartType.Pie]: `pie`,
  [ChartType.RetentionLine]: `retention-curve`,
  [ChartType.RetentionTrend]: `line`,
  [ChartType.RetentionCurve]: `retention-curve`,
  [ChartType.RetentionTrendMetric]: `single-metric`,
  [ChartType.Sankey]: `top-paths`,
  [ChartType.StackedBar]: `bar-stacked`,
  [ChartType.StackedLine]: `stacked-line`,
  [ChartType.Table]: `table`,
};

export const REPORT_TYPE_NAME = {
  [BookmarkType.Flows]: `Flow`,
  [BookmarkType.Funnels]: `Funnel`,
  [BookmarkType.Insights]: `Insights`,
  [BookmarkType.Retention]: `Retention`,
};

export const CHART_TYPE_NAME = {
  [ChartType.Bar]: `Bar`,
  [ChartType.FlowsTopPaths]: `Top Paths`,
  [ChartType.FunnelFrequencyBar]: `Frequency Bar`,
  [ChartType.FunnelFrequencyLine]: `Frequency Line`,
  [ChartType.FunnelMedianTTC]: `Median Time to Convert`,
  [ChartType.FunnelSteps]: `Steps`,
  [ChartType.FunnelStepsMetric]: `Metric`,
  [ChartType.FunnelTopPaths]: `Top Paths`,
  [ChartType.FunnelTrend]: `Trend`,
  [ChartType.FunnelTTCBar]: `Time to Convert Bar`,
  [ChartType.FunnelTTCLine]: `Time to Convert Line`,
  [ChartType.Metric]: `Metric`,
  [ChartType.Line]: `Line`,
  [ChartType.Column]: `Column`,
  [ChartType.StackedColumn]: `Stacked Column`,
  [ChartType.Pie]: `Pie`,
  [ChartType.RetentionLine]: `Retention Curve`,
  [ChartType.RetentionTrend]: `Trend`,
  [ChartType.RetentionTrendMetric]: `Metric`,
  [ChartType.Sankey]: `User Flows`,
  [ChartType.StackedBar]: `Stacked Bar`,
  [ChartType.StackedLine]: `Stacked Line`,
  [ChartType.Table]: `Table`,
  [ChartType.RetentionCurve]: `Retention Curve`,
};

// KEEP ROUGHLY IN SYNC WITH analytics/perf/dashboard_cache_warmer/query/reports/query_params.py QUERY_TO_CHART_TYPE
export const INSIGHTS_CHART_TO_DISPLAY = {
  [ChartType.Bar]: {chartType: ChartType.Bar, plotStyle: ChartPlotStyle.Standard},
  [ChartType.Line]: {chartType: ChartType.Line, plotStyle: ChartPlotStyle.Standard},
  [ChartType.StackedBar]: {chartType: ChartType.Bar, plotStyle: ChartPlotStyle.Stacked},
  [ChartType.StackedLine]: {chartType: ChartType.Line, plotStyle: ChartPlotStyle.Stacked},
  [ChartType.Pie]: {chartType: ChartType.Pie, plotStyle: ChartPlotStyle.Standard},
  [ChartType.Table]: {chartType: ChartType.Table, plotStyle: ChartPlotStyle.Standard},
};

export const REPORT_TYPE_TO_REPORT_NAME: Partial<Record<BookmarkType, ReportName>> = {
  [BookmarkType.Flows]: ReportName.Flows,
  [BookmarkType.Funnels]: ReportName.Funnels,
  [BookmarkType.Insights]: ReportName.Insights,
  [BookmarkType.Retention]: ReportName.Retention,
};
